/**
 * 7h3 Agency — Composio frontend service
 * Wraps all /api/composio/* calls for use in React components.
 */
import apiService from './api.js';

class ComposioService {

  // ── Connections ─────────────────────────────────────────────────────────────

  async getConnections() {
    try {
      return await apiService.get('/composio/connections');
    } catch {
      return { connections: [], configured: false };
    }
  }

  /**
   * Initiate OAuth for a toolkit.
   * Opens the Composio auth URL in a popup and resolves when the user finishes.
   */
  async connectToolkit(toolkit) {
    const res = await apiService.post(`/composio/connect/${toolkit}`, {});
    if (!res.authUrl) throw new Error('No auth URL returned');

    return new Promise((resolve, reject) => {
      const popup = window.open(res.authUrl, 'composio_auth', 'width=600,height=700,scrollbars=yes');
      if (!popup) { reject(new Error('Popup blocked — allow popups for flow.7h3.agency')); return; }

      const TOOLKIT_RE = /^[a-z0-9_-]{1,64}$/i;

      const onMessage = async (e) => {
        // Only accept messages from our own origin, sent by the popup we opened
        if (e.origin !== window.location.origin) return;
        if (e.source !== popup) return;
        if (e.data?.type !== 'composio_connected') return;

        const receivedToolkit = e.data.toolkit;
        if (!TOOLKIT_RE.test(receivedToolkit)) return;

        window.removeEventListener('message', onMessage);
        await apiService.post(`/composio/connect/${receivedToolkit}/activate`, {}).catch(() => {});
        resolve({ toolkit: receivedToolkit, id: e.data.id });
      };
      window.addEventListener('message', onMessage);

      // Timeout after 5 minutes
      setTimeout(() => {
        window.removeEventListener('message', onMessage);
        reject(new Error('Connection timed out'));
      }, 300_000);
    });
  }

  // ── Tools ───────────────────────────────────────────────────────────────────

  async getTools(toolkit = null) {
    const params = toolkit ? { toolkit } : {};
    try {
      const res = await apiService.get('/composio/tools', params);
      return res.tools || [];
    } catch {
      return [];
    }
  }

  /** Group tools by toolkit for the sidebar panel */
  async getToolsByToolkit() {
    const tools = await this.getTools();
    return tools.reduce((acc, t) => {
      (acc[t.toolkit] ??= []).push(t);
      return acc;
    }, {});
  }

  // ── Execution ───────────────────────────────────────────────────────────────

  async execute(slug, args = {}, context = {}) {
    return apiService.post('/composio/execute', { slug, args, ...context });
  }

  // ── History ─────────────────────────────────────────────────────────────────

  async getExecutionHistory() {
    try {
      const res = await apiService.get('/composio/executions');
      return res.executions || [];
    } catch {
      return [];
    }
  }
}

export const composioService = new ComposioService();
export default composioService;
